/**
 * Table Components
 * Consistent table styling for data displays
 */

import React from 'react';
import { theme } from '../../theme';

// Add style tag for table border radius
const tableStyles = `
  .turn-connect-table tbody tr:last-child td:first-child {
    border-bottom-left-radius: ${theme.borders.card.radius};
  }
  .turn-connect-table tbody tr:last-child td:last-child {
    border-bottom-right-radius: ${theme.borders.card.radius};
  }
`;

export interface TableProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  /**
   * Minimum width for the inner table. When set, the table keeps this width and
   * the wrapper scrolls horizontally instead of squeezing columns — the pattern
   * the report grids use so wide tables stay readable on small screens.
   */
  minWidth?: number | string;
}

export interface TableHeaderProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

// Allow callers to attach refs and pass through native attributes (e.g.
// `data-batch-id`) so panels can locate / scroll-into-view a specific row.
export interface TableRowProps extends Omit<React.HTMLAttributes<HTMLTableRowElement>, 'onClick' | 'onMouseEnter' | 'onMouseLeave'> {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  [key: `data-${string}`]: string | undefined;
}

export interface TableCellProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  header?: boolean;
  width?: string;
  colSpan?: number;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  /** Sort state of a sortable column header, announced by screen readers. */
  'aria-sort'?: React.AriaAttributes['aria-sort'];
}

/**
 * Table component for data displays
 */
export const Table: React.FC<TableProps> = ({ children, style, className = '', minWidth }) => {
  return (
    <>
      <style>{tableStyles}</style>
      <div
        style={{
          background: theme.colors.background.secondary,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: theme.borders.card.radius,
          // `auto` (not `hidden`) so a table wider than its container scrolls
          // horizontally instead of being clipped, while still clipping the
          // rounded corners.
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxShadow: theme.shadows.card.default,
          ...style,
        }}
        className={`turn-connect-table ${className}`}
      >
        <table
          style={{
            width: '100%',
            minWidth,
            borderCollapse: 'collapse',
            borderRadius: theme.borders.card.radius,
          }}
        >
          {children}
        </table>
      </div>
    </>
  );
}; /**
 * Table Header component
 */
export const TableHeader: React.FC<TableHeaderProps> = ({ children, style }) => {
  return (
    <thead>
      <tr
        style={{
          background: theme.colors.background.tertiary,
          borderBottom: `1px solid ${theme.colors.border.default}`,
          ...style,
        }}
      >
        {children}
      </tr>
    </thead>
  );
};

/**
 * Table Body component
 */
export const TableBody: React.FC<TableHeaderProps> = ({ children, style }) => {
  return <tbody style={style}>{children}</tbody>;
};

/**
 * Table Row component
 */
export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, style, onClick, onMouseEnter, onMouseLeave, ...rest }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <tr
        ref={ref}
        {...rest}
        style={{
          background: isHovered ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'background-color 0.2s ease',
          ...style,
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          setIsHovered(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setIsHovered(false);
          onMouseLeave?.(e);
        }}
      >
        {children}
      </tr>
    );
  }
);
TableRow.displayName = 'TableRow';

/**
 * Table Cell component
 */
export const TableCell: React.FC<TableCellProps> = ({
  children,
  align = 'left',
  header = false,
  width,
  colSpan,
  style,
  onClick,
  'aria-sort': ariaSort,
}) => {
  const Cell = header ? 'th' : 'td';

  const baseStyles: React.CSSProperties = {
    padding: header ? `${theme.spacing[2]}px ${theme.spacing[3]}px` : `${theme.spacing[2]}px ${theme.spacing[3]}px`,
    textAlign: align,
    color: header ? theme.colors.text.tertiary : theme.colors.text.primary,
    fontSize: header ? theme.typography.fontSize.xs : theme.typography.fontSize.md,
    textTransform: header ? 'uppercase' : 'none',
    fontWeight: header ? theme.typography.fontWeight.bold : theme.typography.fontWeight.normal,
    letterSpacing: header ? theme.typography.letterSpacing.wide : 'normal',
    width: width,
    borderRight: `1px solid ${theme.colors.border.default}`,
    borderBottom: header ? `1px solid ${theme.colors.border.default}` : `1px solid rgba(255, 255, 255, 0.08)`,
  };

  return (
    <Cell style={{ ...baseStyles, ...style }} colSpan={colSpan} onClick={onClick} aria-sort={ariaSort}>
      {children}
    </Cell>
  );
};

export default Table;
